import type { CheckoutFormData } from "@/lib/validations/checkout";
import type { BillingAddressSnapshot } from "@/lib/pfaBillingProfiles";
import { isBillingAddressComplete, normalizeBillingAddressSnapshot } from "@/lib/pfaBillingProfiles";
import { formatCurrency, type CurrencyCode } from "@/lib/utils/currency";
import {
  buildDietaryCommentForWizard,
  formatFlightLegContextForPrompt,
  normalizeSpecialPassengerDraft,
  defaultMenuPreferencesState,
  normalizeMenuPreferencesState,
  type MenuPreferencesState,
  type SpecialPassengerDraft,
} from "@/lib/aiOrderWizardPayload";

export const PREFLIGHT_ORDER_STORAGE_KEY = "pfa_preflight_order_draft_v1";

export type PreflightBudgetScope = "per_person" | "total";

export type PreflightPassengerDietaryDraft = {
  boardDietaryMode: "all_clear" | "some_needs";
  groupAllergyIds: string[];
  groupDietIds: string[];
  groupAllergyOther: string;
  groupDietOther: string;
  groupPreferences: string;
  specialPassengers: SpecialPassengerDraft[];
  includeCrewMeals: boolean;
  /** Optional crew headcount (for displaying + catering quantities). */
  crewCount: number | null;
  /** Optional spending target for caterers. */
  setBudget: boolean;
  budgetAmount: number | null;
  budgetScope: PreflightBudgetScope;
  budgetCurrency: string;
  menuPreferences: MenuPreferencesState;
};

export type PreflightOrderDraft = {
  version: 1 | 2;
  deliveryDate: string;
  deliveryTime: string;
  airportId: string;
  /** Optional estimated block time (e.g. 1h45). */
  estimatedFlightTime?: string | null;
  tailNumber: string;
  fbo: string;
  deliveryOption: CheckoutFormData["deliveryOption"];
  equipment: NonNullable<CheckoutFormData["equipment"]>;
  registrationNumber: string;
  /** Total passengers (adults + children); kept for DB/checkout compatibility. */
  numberOfPassengers: number | null;
  /** Headcount breakdown from the Passengers step (defaults to all adults when absent in legacy JSON). */
  adultPassengers: number;
  childPassengers: number;
  notesToCaterer: string;
  /** Cached label for servicing airport (optional). */
  departureAirportLabel?: string | null;
  /** Destination airport (v2). */
  arrivalAirportId?: string | null;
  arrivalAirportLabel?: string | null;
  /** Structured passenger / dietary state from wizard step 2 (v2). */
  passengerDietary?: PreflightPassengerDietaryDraft | null;
  /** Billing address selected in wizard step 4 (v2). */
  billingAddress?: BillingAddressSnapshot | null;
};

function formatDeliveryDateTimeForLeg(deliveryDate: string, deliveryTime: string): string {
  try {
    const iso = `${deliveryDate}T${deliveryTime}:00`;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return `${deliveryDate} · ${deliveryTime}`;
    return dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return `${deliveryDate} · ${deliveryTime}`;
  }
}

/** Human-readable budget for order UI, checkout, and caterer notes. */
export function formatPreflightBudgetLabel(
  pd:
    | Pick<
        PreflightPassengerDietaryDraft,
        "setBudget" | "budgetAmount" | "budgetScope" | "budgetCurrency"
      >
    | null
    | undefined,
): string | null {
  if (!pd?.setBudget || pd.budgetAmount == null || !(pd.budgetAmount > 0)) {
    return null;
  }
  const scopeLabel = pd.budgetScope === "per_person" ? "per person" : "total for order";
  const amount = formatCurrency(pd.budgetAmount, pd.budgetCurrency as CurrencyCode);
  return `${amount} (${scopeLabel})`;
}

export function defaultPreflightPassengerDietary(): PreflightPassengerDietaryDraft {
  return {
    boardDietaryMode: "some_needs",
    groupAllergyIds: [],
    groupDietIds: [],
    groupAllergyOther: "",
    groupDietOther: "",
    groupPreferences: "",
    specialPassengers: [],
    includeCrewMeals: false,
    crewCount: null,
    setBudget: false,
    budgetAmount: null,
    budgetScope: "total",
    budgetCurrency: "USD",
    menuPreferences: defaultMenuPreferencesState(),
  };
}

/** Normalizes legacy drafts; dietary groups may be empty until the user adds one. */
export function normalizePreflightPassengerDietary(
  pd: Partial<PreflightPassengerDietaryDraft> | null | undefined,
): PreflightPassengerDietaryDraft {
  const base = defaultPreflightPassengerDietary();
  if (!pd || typeof pd !== "object") return base;
  const specs = (pd.specialPassengers ?? []).map(normalizeSpecialPassengerDraft);
  const crewCountRaw = (pd as { crewCount?: unknown }).crewCount;
  const crewCount =
    typeof crewCountRaw === "number"
      ? Math.max(0, Math.min(50, Math.floor(crewCountRaw)))
      : typeof crewCountRaw === "string"
        ? Math.max(0, Math.min(50, Math.floor(Number.parseInt(crewCountRaw, 10))))
        : null;
  const setBudget = Boolean((pd as { setBudget?: unknown }).setBudget);
  const budgetAmountRaw = (pd as { budgetAmount?: unknown }).budgetAmount;
  const budgetAmount =
    typeof budgetAmountRaw === "number" && Number.isFinite(budgetAmountRaw)
      ? Math.max(0, budgetAmountRaw)
      : typeof budgetAmountRaw === "string" && budgetAmountRaw.trim() !== ""
        ? Math.max(0, Number.parseFloat(budgetAmountRaw))
        : null;
  const scopeRaw = (pd as { budgetScope?: unknown }).budgetScope;
  const budgetScope: PreflightBudgetScope =
    scopeRaw === "per_person" || scopeRaw === "total" ? scopeRaw : base.budgetScope;
  const currencyRaw = (pd as { budgetCurrency?: unknown }).budgetCurrency;
  const budgetCurrency =
    typeof currencyRaw === "string" && currencyRaw.trim() ? currencyRaw.trim().toUpperCase() : base.budgetCurrency;
  return {
    ...base,
    ...pd,
    boardDietaryMode: "some_needs",
    specialPassengers: specs,
    crewCount,
    setBudget,
    budgetAmount: setBudget ? budgetAmount : null,
    budgetScope,
    budgetCurrency,
    menuPreferences: normalizeMenuPreferencesState(pd.menuPreferences),
  };
}

/** Build caterer notes from wizard passenger step + flight legs (call on final save). */
export function buildNotesToCatererFromPreflightWizard(input: {
  deliveryDate: string;
  deliveryTime: string;
  departureLabel: string;
  arrivalLabel: string;
  passengerDietary: PreflightPassengerDietaryDraft;
  estimatedFlightTime?: string;
  equipmentNotesHint?: string;
  /** Microwave / fridge / oven / storage summary for caterer notes. */
  equipmentSummary?: string | null;
  adultPassengers: number;
  childPassengers: number;
}): string {
  const dep = input.departureLabel.trim() || "—";
  const arr = input.arrivalLabel.trim() || "—";
  const flightLegContext = input.arrivalLabel.trim()
    ? formatFlightLegContextForPrompt({
        departureDateTime: formatDeliveryDateTimeForLeg(input.deliveryDate, input.deliveryTime),
        departureLabel: dep,
        arrivalLabel: arr,
      })
    : null;

  const pd = input.passengerDietary;
  const allClear = pd.boardDietaryMode === "all_clear";

  const base = buildDietaryCommentForWizard({
    preflightNotes: input.equipmentNotesHint?.trim() ?? "",
    flightLegContext,
    groupAllergyIds: allClear ? [] : pd.groupAllergyIds,
    groupDietIds: allClear ? [] : pd.groupDietIds,
    groupAllergyOther: allClear ? "" : pd.groupAllergyOther,
    groupDietOther: allClear ? "" : pd.groupDietOther,
    groupPreferences: allClear ? "" : pd.groupPreferences,
    specialPassengers: allClear ? [] : pd.specialPassengers,
    includeCrewMeals: pd.includeCrewMeals,
    menuPreferences: pd.menuPreferences,
  });
  const extra: string[] = [];
  const est = (input.estimatedFlightTime ?? "").trim();
  if (est) extra.push(`Estimated flight time: ${est}`);
  if (typeof pd.crewCount === "number" && pd.crewCount > 0) {
    extra.push(`Crew count: ${pd.crewCount}`);
  }
  const budgetLabel = formatPreflightBudgetLabel(pd);
  if (budgetLabel) {
    extra.push(`Budget: ${budgetLabel}`);
  }
  const equipmentSummary = (input.equipmentSummary ?? "").trim();
  if (equipmentSummary) {
    extra.push(`Galley equipment: ${equipmentSummary}`);
  } else if (input.equipmentNotesHint?.trim()) {
    extra.push(`Galley equipment notes: ${input.equipmentNotesHint.trim()}`);
  }
  const a = Math.max(0, Math.min(500, Math.floor(input.adultPassengers)));
  const c = Math.max(0, Math.min(500, Math.floor(input.childPassengers)));
  if (a + c > 0) {
    extra.push(`Passenger headcount: ${a} adult(s), ${c} child(ren) (${a + c} total)`);
  }
  return [base, extra.join("\n")].filter((x) => x.trim()).join("\n\n");
}

export function savePreflightDraft(draft: PreflightOrderDraft): void {
  try {
    sessionStorage.setItem(PREFLIGHT_ORDER_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* ignore quota */
  }
}

function migrateParsed(parsed: Record<string, unknown>): PreflightOrderDraft | null {
  const ver = parsed.version;
  const versionNum = ver === 2 ? 2 : ver === 1 ? 1 : 1;
  if (ver != null && ver !== 1 && ver !== 2) return null;

  const airportId = typeof parsed.airportId === "string" ? parsed.airportId : "";
  if (!airportId) return null;

  const base: PreflightOrderDraft = {
    version: versionNum,
    deliveryDate: typeof parsed.deliveryDate === "string" ? parsed.deliveryDate : "",
    deliveryTime: typeof parsed.deliveryTime === "string" ? parsed.deliveryTime : "",
    airportId,
    tailNumber: typeof parsed.tailNumber === "string" ? parsed.tailNumber : "",
    fbo: typeof parsed.fbo === "string" ? parsed.fbo : "",
    deliveryOption:
      parsed.deliveryOption === "Delivery" ||
      parsed.deliveryOption === "Pickup" ||
      parsed.deliveryOption === "FBO_Drop_off"
        ? parsed.deliveryOption
        : "Delivery",
    equipment: (parsed.equipment as PreflightOrderDraft["equipment"]) ?? {
      microwave: false,
      microwave_dimensions: "",
      fridge: false,
      fridge_dimensions: "",
      oven: false,
      oven_dimensions: "",
      storage: false,
      storage_dimensions: "",
      other: "",
    },
    registrationNumber:
      typeof parsed.registrationNumber === "string" ? parsed.registrationNumber : "",
    numberOfPassengers:
      typeof parsed.numberOfPassengers === "number" ? parsed.numberOfPassengers : null,
    notesToCaterer: typeof parsed.notesToCaterer === "string" ? parsed.notesToCaterer : "",
    estimatedFlightTime:
      typeof parsed.estimatedFlightTime === "string" ? parsed.estimatedFlightTime : null,
    departureAirportLabel:
      typeof parsed.departureAirportLabel === "string" ? parsed.departureAirportLabel : null,
    arrivalAirportId:
      typeof parsed.arrivalAirportId === "string" ? parsed.arrivalAirportId : null,
    arrivalAirportLabel:
      typeof parsed.arrivalAirportLabel === "string" ? parsed.arrivalAirportLabel : null,
    passengerDietary: null,
    adultPassengers: 1,
    childPassengers: 0,
    billingAddress: null,
  };

  if (parsed.passengerDietary && typeof parsed.passengerDietary === "object") {
    base.passengerDietary = normalizePreflightPassengerDietary(
      parsed.passengerDietary as Partial<PreflightPassengerDietaryDraft>,
    );
  }

  base.billingAddress = normalizeBillingAddressSnapshot(parsed.billingAddress);

  const parseStoredCount = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return Math.floor(v);
    return null;
  };
  let adults = parseStoredCount(parsed.adultPassengers);
  let children = parseStoredCount(parsed.childPassengers);
  if (adults == null && children == null) {
    const tot = base.numberOfPassengers;
    if (tot != null && tot > 0) {
      adults = Math.min(500, Math.max(1, tot));
      children = 0;
    } else {
      adults = 1;
      children = 0;
    }
  } else {
    adults = Math.max(0, Math.min(500, adults ?? 0));
    children = Math.max(0, Math.min(500, children ?? 0));
  }
  if (adults + children < 1) {
    adults = 1;
    children = 0;
  }
  if (adults + children > 500) {
    children = Math.min(children, 500 - adults);
    if (adults + children > 500) adults = Math.max(0, 500 - children);
  }
  base.adultPassengers = adults;
  base.childPassengers = children;
  base.numberOfPassengers = adults + children;

  return base;
}

export function loadPreflightDraft(): PreflightOrderDraft | null {
  try {
    const raw = sessionStorage.getItem(PREFLIGHT_ORDER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return migrateParsed(parsed);
  } catch {
    return null;
  }
}

const DELIVERY_TIME_RE = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
const TAIL_NUMBER_RE = /^[A-Z0-9-]+$/i;

/** Same rules as PreflightOrderWizardDialog steps 1–4 (no toasts). */
export function isPreflightDraftReadyForOrdering(draft: PreflightOrderDraft | null): boolean {
  if (!draft) return false;

  if (!draft.deliveryDate?.trim() || !draft.deliveryTime?.trim()) return false;
  if (!DELIVERY_TIME_RE.test(draft.deliveryTime.trim())) return false;

  try {
    const deliveryDay = new Date(`${draft.deliveryDate}T00:00:00`);
    if (Number.isNaN(deliveryDay.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (deliveryDay < today) return false;
  } catch {
    return false;
  }

  if (!draft.airportId?.trim()) return false;
  if (!draft.arrivalAirportId?.trim()) return false;
  if (draft.arrivalAirportId === draft.airportId) return false;

  const tail = (draft.tailNumber ?? "").trim();
  if (!tail || tail.length > 15 || !TAIL_NUMBER_RE.test(tail)) return false;

  if (!(draft.fbo ?? "").trim()) return false;
  if (
    draft.deliveryOption !== "Delivery" &&
    draft.deliveryOption !== "Pickup" &&
    draft.deliveryOption !== "FBO_Drop_off"
  ) {
    return false;
  }

  if (!isBillingAddressComplete(draft.billingAddress)) return false;

  const adults = typeof draft.adultPassengers === "number" ? draft.adultPassengers : 0;
  const children = typeof draft.childPassengers === "number" ? draft.childPassengers : 0;
  const total = adults + children;
  if (total < 1) return false;

  const pd = draft.passengerDietary;
  if (pd?.specialPassengers?.length) {
    const onDietaryGroups = pd.specialPassengers.reduce(
      (s, sp) => s + (sp.adultCount ?? 0) + (sp.childCount ?? 0),
      0,
    );
    if (onDietaryGroups > total) return false;
  }

  return true;
}

/** Path 2: draft must match the airport list the user is browsing. */
export function canProceedToCatererOrder(
  draft: PreflightOrderDraft | null,
  contextAirportId?: string,
): boolean {
  if (!isPreflightDraftReadyForOrdering(draft)) return false;
  if (contextAirportId?.trim() && draft!.airportId !== contextAirportId) return false;
  return true;
}

export function clearPreflightDraft(): void {
  sessionStorage.removeItem(PREFLIGHT_ORDER_STORAGE_KEY);
}

export function getAiOrderContextFromPreflight(): {
  passengerCount: number;
  microwaveAvailable: boolean;
  dietaryComment: string;
  hasPreflightDraft: boolean;
  passengerFromDraft: boolean;
  hasArrivalAirport: boolean;
  hasPassengerDietaryStep: boolean;
} {
  const draft = loadPreflightDraft();
  const hasPreflightDraft = draft != null;

  let passengerCount = 8;
  let passengerFromDraft = false;
  if (draft?.numberOfPassengers != null && draft.numberOfPassengers > 0) {
    passengerCount = Math.min(500, Math.max(1, draft.numberOfPassengers));
    passengerFromDraft = true;
  }

  const microwaveAvailable = draft?.equipment?.microwave === true;
  const dietaryComment = draft?.notesToCaterer?.trim() ?? "";

  const hasArrivalAirport = Boolean(draft?.arrivalAirportId?.trim());
  const hasPassengerDietaryStep =
    draft?.version === 2 &&
    hasArrivalAirport &&
    draft.numberOfPassengers != null &&
    draft.numberOfPassengers >= 1 &&
    draft.passengerDietary != null;

  return {
    passengerCount,
    microwaveAvailable,
    dietaryComment,
    hasPreflightDraft,
    passengerFromDraft,
    hasArrivalAirport,
    hasPassengerDietaryStep,
  };
}
